import * as angular from "angular";
import * as _ from "lodash";

/**
 * AV-17958: entering an account or an organisation from the admin console,
 * shared by the list rows and the activity history dialog. Mirrors what the
 * lists did inline: join if not yet a member, switch the live session, then
 * re-authenticate and load the destination app.
 *
 * Rejects with a message the caller can toast. Never resolves on success
 * because the page navigates away.
 */
export class AdminEnterService {
    constructor(
        private UserService: any,
        private CampaignService: any,
        private OrganisationService: any,
        private UserDataProvider: any,
        private CredentialsManager: any,
        private $cookies: any,
        private $q: any,
        private $window: any
    ) {}

    enterAccount(campaign: {
        campaignid: number | string;
        member?: any;
    }): Promise<void> {
        const open = (): Promise<void> =>
            this.switchAndOpen(campaign.campaignid, "/app.php#/", null);
        if (campaign.member == 1 || campaign.member == "1") {
            return open();
        }
        return this.UserService.add({ campaignid: campaign.campaignid }).then(
            (): Promise<void> => {
                campaign.member = 1;
                return open();
            },
            // May already be a member: try to switch regardless
            (): Promise<void> => open()
        );
    }

    enterOrganisation(organisation: {
        oid: number | string;
        first_campaignid?: number | string | null;
        member?: any;
    }): Promise<void> {
        if (!organisation.first_campaignid) {
            return this.$q.reject("This organisation has no accounts to open");
        }
        const open = (): Promise<void> =>
            this.switchAndOpen(
                organisation.first_campaignid as number,
                "/leader.php/#/divisions",
                organisation.oid
            );
        if (organisation.member == 1 || organisation.member == "1") {
            return open();
        }
        const data = angular.extend(
            _.cloneDeep(organisation),
            this.UserDataProvider.getActiveUser()
        );
        return this.OrganisationService.addAdmin(data).then(
            (response: any): Promise<void> => {
                const json = response.data;
                if (json && json.success) {
                    organisation.member = 1;
                    return open();
                }
                return this.$q.reject(
                    (json && json.message) || "Could not join the organisation"
                );
            },
            (): Promise<void> =>
                this.$q.reject("Could not join the organisation")
        );
    }

    private switchAndOpen(
        campaignid: number | string,
        destination: string,
        oid: number | string | null
    ): Promise<void> {
        return this.CampaignService.live({ campaignid: campaignid }).then(
            (response: any): Promise<void> => {
                const json: any = response.data;
                if (json.success) {
                    // Matches changeLiveCampaign: the destination app re-runs its
                    // first-login bootstrap.
                    this.$cookies.remove("signedIn");
                    return this.CredentialsManager.reauth(
                        json.campaign.campaignid,
                        oid !== null ? oid : json.campaign.oid || null
                    ).then((): void => {
                        this.$window.location.href = destination;
                    });
                }
                if (json.region && json.handoff) {
                    // Hosted in the other region: the API minted a single-use
                    // token and has already dropped our session here, so
                    // carry it over. Never resolves; the page navigates.
                    this.CredentialsManager.handoff(
                        json.region,
                        json.handoff,
                        destination
                    );
                    return this.$q.defer().promise;
                }
                if (json.region) {
                    return this.$q.reject(
                        "This record is hosted in the '" +
                            json.region +
                            "' region — open it from that region's admin panel"
                    );
                }
                return this.$q.reject(
                    json.message || "Could not switch into this account"
                );
            }
        );
    }
}

angular.module("vcServices").service("AdminEnterService", AdminEnterService);
